import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { ResumeFileType, TransformedCandidateDocument } from "@/types";

const renameResumeFileSchema = z.object({
  title: z.string().min(1, "Required"),
});

export const RenameResumeDialog = ({
  onOpenChange,
  onRenameSuccess,
  open,
  selectedResumeFile,
  transformedCandidateDocument,
}: {
  onOpenChange: (open: boolean) => void;
  onRenameSuccess?: () => void;
  open: boolean;
  selectedResumeFile: ResumeFileType;
  transformedCandidateDocument: Pick<TransformedCandidateDocument, "userId">;
}) => {
  const novaClientUtils = novaClient.useUtils();

  const { mutate: updateResumeFile, isPending: isPendingUpdateResumeFile } =
    novaClient.internal.user.resume.active.update.useMutation();

  const form = useForm<z.infer<typeof renameResumeFileSchema>>({
    defaultValues: {
      title: selectedResumeFile.title,
    },
    resolver: zodResolver(renameResumeFileSchema),
  });

  const handleSubmit = (data: z.infer<typeof renameResumeFileSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Resume rename",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateResumeFile(
      {
        id: selectedResumeFile.id,
        title: data.title,
      },
      {
        onSuccess: (resumeFile) => {
          void novaClientUtils.internal.user.resume.active.listByUserId.invalidate(
            {
              userId: transformedCandidateDocument.userId,
            },
          );
          handleSuccess(resumeFile);
        },
      },
    );
  };

  const handleSuccess = (resumeFile: ResumeFileType) => {
    onOpenChange(false);
    // Reset the form to the new title.
    form.reset({
      title: resumeFile.title,
    });
    onRenameSuccess?.();
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        // Reset the form when the dialog is closed.
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Rename Resume
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPendingUpdateResumeFile}
                type="submit"
              >
                Rename
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
